/**
 * 字典通用工具类
 */

export interface DictOption {
  label: string;
  value: string;
  itemCode: string;
  itemValue: string;
  original: Api.Dict.PublicDictItemResponse;
}

/**
 * 将字典列表转换为下拉组件通用的 Options 格式 [{ label, value, ... }]
 *
 * @param items 字典项数组
 * @param valueKey 使用 item_code 还是 item_value 作为 option.value，默认为 'item_code'
 * @returns 转化后的选项数组
 */
export function dictToOptions(
  items: Api.Dict.PublicDictItemResponse[] = [],
  valueKey: 'item_code' | 'item_value' = 'item_code'
): DictOption[] {
  if (!Array.isArray(items)) return [];
  return items.map((item) => ({
    label: item.item_name,
    value: valueKey === 'item_value' ? item.item_value : item.item_code,
    itemCode: item.item_code,
    itemValue: item.item_value,
    original: item
  }));
}

/**
 * 从字典项列表中根据 item_code 查找对应的 item_name
 *
 * @param items 字典项数组
 * @param itemCode 字典项编码
 * @param fallback 找不到时的回退名称，默认为 itemCode 或 ''
 */
export function getDictLabelFromItems(
  items: Api.Dict.PublicDictItemResponse[] = [],
  itemCode?: string,
  fallback = ''
): string {
  if (!itemCode || !Array.isArray(items)) return fallback;
  const match = items.find((item) => item.item_code === itemCode);
  return match?.item_name || fallback || itemCode;
}

/**
 * 从字典项列表中根据 item_code 查找对应的 item_value
 *
 * @param items 字典项数组
 * @param itemCode 字典项编码
 * @param fallback 找不到时的回退值
 */
export function getDictValueFromItems(
  items: Api.Dict.PublicDictItemResponse[] = [],
  itemCode?: string,
  fallback = ''
): string {
  if (!itemCode || !Array.isArray(items)) return fallback;
  const match = items.find((item) => item.item_code === itemCode);
  return match?.item_value || fallback;
}

/**
 * 将字典项数组转换为 Map Key-Value 映射对象 (item_code -> item_name)
 *
 * @param items 字典项数组
 * @returns 映射字典 Map
 */
export function dictToLabelMap(
  items: Api.Dict.PublicDictItemResponse[] = []
): Record<string, string> {
  if (!Array.isArray(items)) return {};
  return items.reduce<Record<string, string>>((acc, item) => {
    acc[item.item_code] = item.item_name;
    return acc;
  }, {});
}
